import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import API_BASE_URL from "../config";
import { User, Award, Calendar, Bookmark, MessageCircle, ArrowUp, Edit2, Check, X, Camera, FileText, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "../components/ui/ImageUpload";
import FollowersModal from "../components/FollowersModal";

const AVATAR_CATEGORIES = {
  Mountains: ["🏔", "🗻", "⛰", "🌋"],
  Nature: ["🌿", "🌲", "🍃", "🌸"],
  Adventure: ["🧗", "🏕", "🎒", "🧭"],
  Spiritual: ["🙏", "🕉", "☸", "🪔"]
};

const getEmojiAvatarUrl = (emoji) => {
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f2f0eb' rx='50'/%3E%3Ctext x='50' y='50' font-size='50' text-anchor='middle' dominant-baseline='central'%3E${emoji}%3C/text%3E%3C/svg%3E`;
};

const API = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;

const BADGE_COLORS = {
  admin: "bg-red-100 text-red-700 border-red-200",
  local_expert: "bg-[#F59E0B]/10 text-[#D97706] border-[#F59E0B]/20",
  active_reviewer: "bg-[#3FAF7A]/10 text-[#3FAF7A] border-[#3FAF7A]/20",
  creator: "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20",
  new_traveler: "bg-[#E2E8E4] text-[#5C6B64] border-[#E2E8E4]",
  explorer: "bg-[#123D2B]/10 text-[#123D2B] border-[#123D2B]/20",
  early_traveler: "bg-[#A855F7]/10 text-[#7E22CE] border-[#A855F7]/20",
  founder: "bg-[#111827]/10 text-[#111827] border-[#111827]/20",
};

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const [savedPosts, setSavedPosts] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [tab, setTab] = useState("overview");
  const [isEditing, setIsEditing] = useState(false);
  const [editBio, setEditBio] = useState("");
  const [editAvatar, setEditAvatar] = useState("");
  const [editName, setEditName] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [userStats, setUserStats] = useState({ posts_count: 0, contributions_count: 0 });

  // ── Followers/Following modal state ─────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("followers"); // "followers" | "following"

  const openFollowers = () => { setModalMode("followers"); setModalOpen(true); };
  const openFollowing = () => { setModalMode("following"); setModalOpen(true); };

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/users/${user.id}`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setUserStats({
        posts_count: data.posts_count || 0,
        contributions_count: data.contributions_count || 0,
      });
    } catch {}
  }, [user]);

  const loadSaved = useCallback(async () => {
    setLoadingSaved(true);
    try {
      const { data } = await axios.get(`${API}/users/saved-posts`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setSavedPosts(data.posts || []);
    } catch {
      toast.error("Failed to load saved posts");
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) fetchStats();
  }, [user, fetchStats]);

  useEffect(() => {
    if (user && user !== false && tab === "saved") loadSaved();
  }, [user, tab, loadSaved]);

  const handleEditClick = () => {
    setEditBio(user.bio || "");
    setEditAvatar(user.avatar_path || "");
    setIsEditing(true);
  };

  const handleUpdateProfile = async () => {
    setUpdating(true);
    try {
      await axios.put(
        `${API}/users/update`,
        { bio: editBio, avatar_path: editAvatar },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success("Profile updated successfully");
      setIsEditing(false);
      setIsAvatarModalOpen(false);
      await checkAuth();
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleNameSave = async () => {
    const regex = /^[a-zA-Z0-9\s-]+$/;
    if (editName.length < 2 || editName.length > 30) {
      return toast.error("Name must be between 2 and 30 characters");
    }
    if (!regex.test(editName)) {
      return toast.error("No special characters allowed except spaces and hyphens");
    }

    try {
      await axios.put(
        `${API}/users/update`,
        { name: editName },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success("Name updated successfully!");
      setIsEditingName(false);
      await checkAuth();
    } catch {
      toast.error("Failed to update name");
    }
  };

  const handleAvatarSelect = async (url) => {
    setEditAvatar(url);
    try {
      await axios.put(
        `${API}/users/update`,
        { avatar_path: url },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success("Avatar updated successfully!");
      setIsAvatarModalOpen(false);
      await checkAuth();
    } catch {
      toast.error("Failed to update avatar");
    }
  };

  if (!user || user === false) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="profile-page">

      {/* ── Profile Card ─────────────────────────────────────────── */}
      <div className="bg-white border border-card-border rounded-[24px] shadow-warm p-5 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5">

          {/* Avatar */}
          <div className="relative">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-forest-green/10 flex items-center justify-center text-forest-green text-3xl font-bold shrink-0 relative overflow-hidden border-2 border-forest-green">
              {user.avatar_path ? (
                <img src={user.avatar_path} alt="avatar" className="w-full h-full object-cover" />
              ) : (
                (user.name || user.username || "U").charAt(0).toUpperCase()
              )}
            </div>
            <button
              onClick={() => setIsAvatarModalOpen(true)}
              className="absolute bottom-0 right-0 p-1.5 bg-forest-green text-white rounded-full shadow-lg border-2 border-white hover:bg-[#157a58] transition-colors"
            >
              <Camera className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 w-full min-w-0">
            <div className="flex flex-wrap justify-between items-start gap-2">
              <div className="min-w-0">
                {isEditingName ? (
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="border border-card-border rounded px-2 py-1 text-sm outline-none focus:border-forest-green w-48"
                      autoFocus
                    />
                    <button onClick={handleNameSave} className="text-xs bg-forest-green text-white px-2 py-1 rounded">Save</button>
                    <button onClick={() => setIsEditingName(false)} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">Cancel</button>
                  </div>
                ) : (
                  <div>
                    <h1 className="text-xl sm:text-2xl font-semibold text-text-dark tracking-tight truncate font-display">
                      {user.name || user.username}
                    </h1>
                    <button
                      onClick={() => { setEditName(user.name || user.username); setIsEditingName(true); }}
                      className="text-xs text-forest-green font-medium flex items-center gap-1 hover:underline mt-0.5"
                    >
                      <Edit2 className="w-3 h-3" /> Edit name
                    </button>
                  </div>
                )}
                <p className="text-text-secondary-dark text-sm truncate mt-1">{user.email}</p>

                {/* ── Followers / Following — now clickable ── */}
                <div className="flex gap-4 mt-2">
                  <button
                    onClick={openFollowers}
                    className="text-sm font-medium hover:underline text-left bg-transparent border-0 p-0 cursor-pointer"
                  >
                    <strong className="text-text-dark">{user.followers_count || 0}</strong>
                    <span className="text-text-secondary-dark"> followers</span>
                  </button>
                  <button
                    onClick={openFollowing}
                    className="text-sm font-medium hover:underline text-left bg-transparent border-0 p-0 cursor-pointer"
                  >
                    <strong className="text-text-dark">{user.following_count || 0}</strong>
                    <span className="text-text-secondary-dark"> following</span>
                  </button>
                </div>
              </div>

              {/* Edit / Save buttons */}
              {!isEditing ? (
                <button onClick={handleEditClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-forest-green/5 rounded-lg text-sm font-medium text-forest-green transition-colors border border-card-border shrink-0">
                  <Edit2 className="w-3.5 h-3.5" /> Edit Profile
                </button>
              ) : (
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setIsEditing(false)} className="p-1.5 hover:bg-black/5 rounded-lg text-text-secondary-dark">
                    <X className="w-4 h-4" />
                  </button>
                  <button onClick={handleUpdateProfile} disabled={updating}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-forest-green text-forest-green-foreground hover:bg-[#2D6E58] rounded-lg text-sm font-medium transition-colors">
                    {updating ? "Saving..." : <><Check className="w-3.5 h-3.5" /> Save</>}
                  </button>
                </div>
              )}
            </div>

            {/* Edit form */}
            {isEditing ? (
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-text-secondary-dark mb-1">Bio</label>
                  <textarea value={editBio} onChange={(e) => setEditBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    className="w-full bg-himalayan-mist border border-card-border rounded-lg px-3 py-2 text-sm text-text-dark focus:ring-2 focus:ring-forest-green outline-none transition-all min-h-[80px]" />
                </div>
              </div>
            ) : (
              user.bio && <p className="text-text-dark mt-2 whitespace-pre-wrap text-sm">{user.bio}</p>
            )}

            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="flex items-center gap-1.5 text-xs bg-forest-green/10 text-forest-green border border-primary/20 px-2.5 py-1 rounded-full font-bold uppercase tracking-wide">
                <User className="w-3 h-3" strokeWidth={1.5} /> {user.role === "admin" ? "Admin" : "User"}
              </span>
              {(Array.isArray(user.badges) ? user.badges : (user.badges ? [user.badges] : [])).map((badge) => (
                <span key={badge} className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-bold uppercase tracking-wide border ${BADGE_COLORS[badge] || BADGE_COLORS.new_traveler}`}>
                  <Award className="w-3 h-3" strokeWidth={1.5} /> {typeof badge === 'string' ? badge.replace(/_/g, " ") : String(badge)}
                </span>
              ))}
              <span className="flex items-center gap-1.5 text-xs text-text-secondary-dark">
                <Calendar className="w-3 h-3" strokeWidth={1.5} />
                Joined {new Date(user.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats ────────────────────────────────────────────────── */}
      <div className="mt-4 grid grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white border border-card-border rounded-[24px] shadow-warm p-4 sm:p-5 text-center">
          <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-forest-green mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-xl sm:text-2xl font-bold text-text-dark">{userStats.posts_count}</p>
          <p className="text-xs text-text-secondary-dark mt-1">Posts Created</p>
        </div>
        <div className="bg-white border border-card-border rounded-[24px] shadow-warm p-4 sm:p-5 text-center">
          <Award className="w-5 h-5 sm:w-6 sm:h-6 text-forest-green mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-xl sm:text-2xl font-bold text-text-dark">{Array.isArray(user.badges) ? user.badges.length : (user.badges ? 1 : 0)}</p>
          <p className="text-xs text-text-secondary-dark mt-1">Badges</p>
        </div>
        <div className="bg-white border border-card-border rounded-[24px] shadow-warm p-4 sm:p-5 text-center">
          <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-[#F59E0B] mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-xl sm:text-2xl font-bold text-text-dark">{userStats.contributions_count}</p>
          <p className="text-xs text-text-secondary-dark mt-1">Contributions</p>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="flex gap-1 mt-6 mb-5 bg-white border border-card-border rounded-full p-0.5 w-fit">
        <button onClick={() => setTab("overview")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "overview" ? "bg-forest-green text-forest-green-foreground" : "text-text-secondary-dark hover:bg-black/5"}`}>
          Overview
        </button>
        <button onClick={() => setTab("saved")}
          className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === "saved" ? "bg-forest-green text-forest-green-foreground" : "text-text-secondary-dark hover:bg-black/5"}`}>
          Saved Posts ({user.saved_posts?.length || 0})
        </button>
      </div>

      {/* ── Overview ─────────────────────────────────────────────── */}
      {tab === "overview" && (
        <div className="bg-white border border-card-border rounded-[24px] shadow-warm p-6">
          {userStats.posts_count === 0 && userStats.contributions_count === 0 ? (
            <div className="text-center">
              <p className="text-text-secondary-dark">Your activity overview will appear here as you contribute to the community.</p>
              <Link to="/channels" className="text-forest-green font-medium text-sm mt-2 inline-block hover:underline">
                Start exploring channels
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-semibold text-text-dark">Your Activity</h3>
              <div className="flex items-center justify-between py-2 border-b border-card-border">
                <span className="text-sm text-text-secondary-dark">Posts written</span>
                <span className="font-semibold text-text-dark">{userStats.posts_count}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-card-border">
                <span className="text-sm text-text-secondary-dark">Total contributions</span>
                <span className="font-semibold text-text-dark">{userStats.contributions_count}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-text-secondary-dark">Badges earned</span>
                <span className="font-semibold text-text-dark">{Array.isArray(user.badges) ? user.badges.length : (user.badges ? 1 : 0)}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Saved Posts ──────────────────────────────────────────── */}
      {tab === "saved" && (
        <div className="space-y-3">
          {loadingSaved ? (
            <div className="animate-pulse space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 bg-black/5 rounded-[16px]" />
              ))}
            </div>
          ) : savedPosts.length === 0 ? (
            <div className="bg-white border border-card-border rounded-[24px] shadow-warm p-8 text-center">
              <Bookmark className="w-10 h-10 text-muted mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-text-secondary-dark">No saved posts yet. Save posts you want to revisit later.</p>
            </div>
          ) : (
            savedPosts.map((post) => (
              <Link key={post.id} to={`/posts/${post.id}`}
                className="bg-white border border-card-border rounded-[16px] p-4 flex items-center gap-4 transition-all hover:border-forest-green/50 hover:shadow-sm block">
                <div className="flex flex-col items-center text-text-secondary-dark">
                  <ArrowUp className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-xs font-bold">{post.vote_score || 0}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-text-dark truncate">{post.title}</h3>
                  <div className="flex items-center gap-3 mt-1 text-xs text-text-secondary-dark">
                    <span className="truncate">{post.author?.name || "Unknown"}</span>
                    <span className="flex items-center gap-0.5 shrink-0">
                      <MessageCircle className="w-3 h-3" /> {post.comment_count || 0}
                    </span>
                    <span className="shrink-0">{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
      )}

      {/* ── Followers / Following Modal ───────────────────────────── */}
      <FollowersModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        userId={user.id}
        mode={modalMode}
      />

      {/* ── Avatar Modal ───────────────────────────── */}
      {isAvatarModalOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b border-card-border p-4 flex justify-between items-center z-10">
              <h2 className="text-lg font-bold text-text-dark">Choose an Avatar</h2>
              <button onClick={() => setIsAvatarModalOpen(false)} className="p-2 hover:bg-black/5 rounded-full transition-colors">
                <X className="w-5 h-5 text-text-secondary-dark" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6 space-y-6">
              {Object.entries(AVATAR_CATEGORIES).map(([category, emojis]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-text-secondary-dark mb-3 uppercase tracking-wider">{category}</h3>
                  <div className="grid grid-cols-4 gap-3 sm:gap-4">
                    {emojis.map((emoji, idx) => {
                      const url = getEmojiAvatarUrl(emoji);
                      const isSelected = user.avatar_path === url;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleAvatarSelect(url)}
                          className={`aspect-square rounded-full flex items-center justify-center text-3xl sm:text-4xl hover:scale-105 transition-transform bg-[#f2f0eb] ${
                            isSelected ? "border-[3px] border-[#1D9E75] shadow-md" : "border border-transparent"
                          }`}
                        >
                          {emoji}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div>
                <h3 className="text-sm font-semibold text-text-secondary-dark mb-3 uppercase tracking-wider">Custom</h3>
                <div className="border-2 border-dashed border-card-border rounded-xl p-4 bg-gray-50 flex flex-col items-center justify-center">
                  <ImageUpload
                    currentImage={null}
                    onUploadSuccess={(url) => handleAvatarSelect(url)}
                    onRemove={() => {}}
                  />
                  <p className="text-xs text-text-secondary-dark mt-2 text-center max-w-[200px]">Upload a photo from your device</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
